import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  TextField,
  CircularProgress,
  InputAdornment,
  Slider,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  Percent as PercentIcon,
  TrendingUp as TrendingUpIcon,
  Info as InfoIcon,
  Save as SaveIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { PlatformSettingsService, PlatformFeeSettings } from '../../../services/platformSettingsService';

const PlatformFeesTab: React.FC = () => {
  const [feeSettings, setFeeSettings] = useState<PlatformFeeSettings>({
    fee_type: 'fixed',
    fee_amount: 0.50,
    fee_percentage: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalSettings, setOriginalSettings] = useState<PlatformFeeSettings>({
    fee_type: 'fixed',
    fee_amount: 0.50,
    fee_percentage: null,
  });

  // Load platform fee settings on component mount
  useEffect(() => {
    loadPlatformFeeSettings();
  }, []);

  const loadPlatformFeeSettings = async () => {
    try {
      setLoading(true);
      const settings = await PlatformSettingsService.getPlatformFeeSettings();
      setFeeSettings(settings);
      setOriginalSettings(settings);
      setHasChanges(false);
    } catch (error) {
      console.error('Error loading platform fee settings:', error);
      toast.error('Failed to load platform fee settings');
    } finally {
      setLoading(false);
    }
  };

  const handleFeeTypeChange = (
    event: React.MouseEvent<HTMLElement>,
    newType: 'fixed' | 'percentage' | null
  ) => {
    if (newType !== null) {
      setFeeSettings(prev => ({
        ...prev,
        fee_type: newType,
      }));
      setHasChanges(true);
    }
  };

  const handleFixedFeeChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = parseFloat(event.target.value) || 0;
    const clampedValue = Math.max(0, Math.min(3, newValue));
    setFeeSettings(prev => ({
      ...prev,
      fee_amount: clampedValue,
    }));
    setHasChanges(true);
  };

  const handleFixedSliderChange = (event: Event, value: number | number[]) => {
    const newValue = typeof value === 'number' ? value : value[0];
    const clampedValue = Math.max(0, Math.min(3, newValue));
    setFeeSettings(prev => ({
      ...prev,
      fee_amount: clampedValue,
    }));
    setHasChanges(true);
  };

  const handlePercentageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = parseFloat(event.target.value) || 0;
    const clampedValue = Math.max(0, Math.min(100, newValue));
    setFeeSettings(prev => ({
      ...prev,
      fee_percentage: clampedValue,
    }));
    setHasChanges(true);
  };

  const handlePercentageSliderChange = (event: Event, value: number | number[]) => {
    const newValue = typeof value === 'number' ? value : value[0];
    const clampedValue = Math.max(0, Math.min(100, newValue));
    setFeeSettings(prev => ({
      ...prev,
      fee_percentage: clampedValue,
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (feeSettings.fee_type === 'fixed') {
      if (feeSettings.fee_amount < 0) {
        toast.error('Platform fee cannot be negative');
        return;
      }
      if (feeSettings.fee_amount > 3) {
        toast.error('Platform fee cannot exceed $3');
        return;
      }
    } else {
      if (feeSettings.fee_percentage === null || feeSettings.fee_percentage < 0) {
        toast.error('Percentage fee cannot be negative');
        return;
      }
      if (feeSettings.fee_percentage > 100) {
        toast.error('Percentage fee cannot exceed 100%');
        return;
      }
    }

    try {
      setSaving(true);
      await PlatformSettingsService.updatePlatformFeeSettings(feeSettings);
      setOriginalSettings(feeSettings);
      setHasChanges(false);
      toast.success('Platform fee settings updated successfully!');
    } catch (error) {
      console.error('Error updating platform fee settings:', error);
      toast.error('Failed to update platform fee settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setFeeSettings(originalSettings);
    setHasChanges(false);
  };

  // Calculate example fees for different purchase amounts
  const calculateExampleFee = (purchaseAmount: number) => {
    if (feeSettings.fee_type === 'fixed') {
      return feeSettings.fee_amount;
    } else {
      return (purchaseAmount * (feeSettings.fee_percentage || 0)) / 100;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 1 }}>
            Platform Fees
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Set the platform fee amount charged for every purchase made on the platform.
          </Typography>
        </Box>
      </Box>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card
          sx={{
            borderRadius: 3,
            border: '2px solid rgba(102, 126, 234, 0.2)',
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
            mb: 4,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  mr: 2,
                  display: 'flex',
                  alignItems: 'center',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                }}
              >
                <MoneyIcon sx={{ color: 'white', fontSize: 32 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                  Fee Calculation Method
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Choose how platform fees are calculated
                </Typography>
              </Box>
            </Box>

            {/* Fee Type Toggle */}
            <Box sx={{ mb: 4 }}>
              <ToggleButtonGroup
                value={feeSettings.fee_type}
                exclusive
                onChange={handleFeeTypeChange}
                aria-label="fee type"
                fullWidth
                sx={{
                  '& .MuiToggleButton-root': {
                    py: 2,
                    px: 3,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    border: '2px solid rgba(102, 126, 234, 0.2)',
                    '&.Mui-selected': {
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: '2px solid transparent',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                      },
                    },
                    '&:not(.Mui-selected)': {
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      color: 'text.primary',
                      '&:hover': {
                        backgroundColor: 'rgba(102, 126, 234, 0.1)',
                      },
                    },
                  },
                }}
              >
                <ToggleButton value="fixed" aria-label="fixed fee">
                  <MoneyIcon sx={{ mr: 1 }} />
                  Fixed Amount
                </ToggleButton>
                <ToggleButton value="percentage" aria-label="percentage fee">
                  <PercentIcon sx={{ mr: 1 }} />
                  Percentage
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <AnimatePresence mode="wait">
              {feeSettings.fee_type === 'fixed' ? (
                <motion.div
                  key="fixed"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                >
                  <Box sx={{ mb: 4 }}>
                    <TextField
                      fullWidth
                      label="Fixed Fee Amount"
                      type="number"
                      value={feeSettings.fee_amount}
                      onChange={handleFixedFeeChange}
                      inputProps={{
                        min: 0,
                        max: 3,
                        step: 0.01,
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MoneyIcon sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                              USD
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          fontSize: '1.25rem',
                          fontWeight: 600,
                          '& input': {
                            textAlign: 'center',
                          },
                        },
                      }}
                    />

                    <Box sx={{ mt: 3, px: 1 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                        Quick Adjust: ${feeSettings.fee_amount.toFixed(2)}
                      </Typography>
                      <Slider
                        value={feeSettings.fee_amount}
                        onChange={handleFixedSliderChange}
                        min={0}
                        max={3}
                        step={0.05}
                        marks={[
                          { value: 0, label: '$0' },
                          { value: 0.25, label: '$0.25' },
                          { value: 0.50, label: '$0.50' },
                          { value: 0.75, label: '$0.75' },
                          { value: 1, label: '$1' },
                          { value: 1.50, label: '$1.50' },
                          { value: 2, label: '$2' },
                          { value: 2.50, label: '$2.50' },
                          { value: 3, label: '$3' },
                        ]}
                        sx={{
                          '& .MuiSlider-thumb': {
                            width: 24,
                            height: 24,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                          },
                          '& .MuiSlider-track': {
                            background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                            height: 6,
                          },
                          '& .MuiSlider-rail': {
                            height: 6,
                            opacity: 0.3,
                          },
                          '& .MuiSlider-markLabel': {
                            fontSize: '0.75rem',
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </motion.div>
              ) : (
                <motion.div
                  key="percentage"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                >
                  <Box sx={{ mb: 4 }}>
                    <TextField
                      fullWidth
                      label="Fee Percentage"
                      type="number"
                      value={feeSettings.fee_percentage || ''}
                      onChange={handlePercentageChange}
                      inputProps={{
                        min: 0,
                        max: 100,
                        step: 0.1,
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PercentIcon sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                              %
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          fontSize: '1.25rem',
                          fontWeight: 600,
                          '& input': {
                            textAlign: 'center',
                          },
                        },
                      }}
                    />

                    <Box sx={{ mt: 3, px: 1 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                        Percentage: {feeSettings.fee_percentage?.toFixed(2) || '0.00'}%
                      </Typography>
                      <Slider
                        value={feeSettings.fee_percentage || 0}
                        onChange={handlePercentageSliderChange}
                        min={0}
                        max={10}
                        step={0.1}
                        marks={[
                          { value: 0, label: '0%' },
                          { value: 1, label: '1%' },
                          { value: 2, label: '2%' },
                          { value: 3, label: '3%' },
                          { value: 5, label: '5%' },
                          { value: 10, label: '10%' },
                        ]}
                        sx={{
                          '& .MuiSlider-thumb': {
                            width: 24,
                            height: 24,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                          },
                          '& .MuiSlider-track': {
                            background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                            height: 6,
                          },
                          '& .MuiSlider-rail': {
                            height: 6,
                            opacity: 0.3,
                          },
                          '& .MuiSlider-markLabel': {
                            fontSize: '0.75rem',
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>

            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={!hasChanges || saving}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  },
                  '&:disabled': {
                    background: 'rgba(0, 0, 0, 0.12)',
                  },
                  textTransform: 'none',
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              {hasChanges && (
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  disabled={saving}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    borderColor: 'text.secondary',
                    color: 'text.secondary',
                  }}
                >
                  Reset
                </Button>
              )}
            </Box>

            <Alert
              icon={<InfoIcon />}
              severity="info"
              sx={{
                borderRadius: 2,
                mt: 3,
                background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(33, 150, 243, 0.05) 100%)',
                border: '1px solid rgba(33, 150, 243, 0.3)',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                How Platform Fees Work
              </Typography>
              <Typography variant="body2">
                {feeSettings.fee_type === 'fixed' ? (
                  <>
                    With a fixed fee of ${feeSettings.fee_amount.toFixed(2)}, every purchase will charge exactly ${feeSettings.fee_amount.toFixed(2)} as the platform fee, regardless of the purchase amount.
                  </>
                ) : (
                  <>
                    With a percentage fee of {feeSettings.fee_percentage?.toFixed(2) || '0.00'}%, the platform fee is calculated as a percentage of the total purchase price. For example, a $100 purchase would result in a ${calculateExampleFee(100).toFixed(2)} platform fee.
                  </>
                )}
              </Typography>
            </Alert>
          </CardContent>
        </Card>

        {/* Example Calculations */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3, mb: 4 }}>
          {[10, 50, 100].map((amount) => (
            <Card
              key={amount}
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(0, 0, 0, 0.05)',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.6) 100%)',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: 'rgba(76, 175, 80, 0.1)',
                      mr: 2,
                    }}
                  >
                    <TrendingUpIcon sx={{ color: '#4CAF50', fontSize: 28 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    ${amount} Purchase
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Platform Fee:
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4CAF50' }}>
                  ${calculateExampleFee(amount).toFixed(2)}
                </Typography>
                {feeSettings.fee_type === 'percentage' && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                    ({feeSettings.fee_percentage?.toFixed(2) || '0.00'}% of ${amount})
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: '1px solid rgba(0, 0, 0, 0.05)',
            background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.05) 0%, rgba(76, 175, 80, 0.02) 100%)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SecurityIcon sx={{ color: '#4CAF50', mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Platform Settings
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            The platform fee settings are applied to all transactions automatically. This setting is shared across all admin users 
            and affects all future purchases on the platform. Changes take effect immediately after saving.
          </Typography>
        </Paper>
      </motion.div>
    </Box>
  );
};

export default PlatformFeesTab;
